import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, RangeTooLargeError } from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateLesserOfChargesMutation } from "store/queries/lesserOfCharges";

import {
  ILesserOfChargesFormData,
  INITIAL_LESSER_OF_CHARGES_FORM_DATA,
  LesserOfChargesForm,
} from "./components/LesserOfChargesForm";

export function CreateLesserOfChargesPage() {
  useDocumentTitle("Create Lesser-Of Charges Rule");
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createMutation, { isLoading }] = useCreateLesserOfChargesMutation();
  const [formData, setFormData] = useState<ILesserOfChargesFormData>(
    INITIAL_LESSER_OF_CHARGES_FORM_DATA
  );

  const handleSubmit = async () => {
    if (!formData.scope) return;
    if (formData.applicability === "SCOPED" && !formData.providerId) return;
    let codes: string[];
    let excludedCodes: string[];
    try {
      codes = prepareCodesForSubmit(formData.codes);
      excludedCodes = prepareCodesForSubmit(formData.excludedCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Range too large",
        detail:
          err instanceof RangeTooLargeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await createMutation({
        scope: formData.scope,
        multiplier: formData.multiplier,
        codes,
        excludedCodes,
        groupName: formData.groupName.trim() || null,
        // CLE-2524: applicability replaces the legacy isDefault flag.
        applicability: formData.applicability,
        providerId:
          formData.applicability === "SCOPED" ? formData.providerId : null,
        // CLE-2597 — a FALLBACK rule may name a payer; only a TYPICAL template is scopeless.
        payerId: formData.applicability === "TYPICAL" ? null : formData.payerId,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Lesser-Of charges rule created successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to create lesser-of charges rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LesserOfChargesForm
      title="Create Lesser-Of Charges Rule"
      submitLabel="Create Rule"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
    />
  );
}
